import { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";

const NAV = [
  { label: "Services", id: "services" },
  { label: "How It Works", id: "how-it-works" },
  { label: "Rates", id: "rates" },
  { label: "Contact", id: "contact" },
];

const scrollTo = (id) =>
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-300 border-b ${
        scrolled
          ? "bg-[#1C0D2C]/80 backdrop-blur-xl border-white/10"
          : "bg-[#12071F]/60 backdrop-blur-md border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
        <button
          data-testid="header-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3 group"
        >
          <img
            src="/bec-logo.jpg"
            alt="BEC Couriers logo"
            className="w-9 h-9 rounded-full object-cover ring-1 ring-white/20"
          />
          <span className="font-display font-800 tracking-tight text-lg sm:text-xl">
            BEC <span className="text-[#FF8A00]">Couriers</span>
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-8" data-testid="header-nav">
          {NAV.map((item) => (
            <button
              key={item.id}
              data-testid={`nav-${item.id}`}
              onClick={() => scrollTo(item.id)}
              className="text-sm text-[#A29DB0] hover:text-[#F8F7FA] transition-colors duration-200"
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            data-testid="header-login-link"
            href="https://bec.tasoko.com/login"
            target="_blank"
            rel="noreferrer"
            title="Current customers — old system"
            className="px-5 py-2 rounded-full border border-white/20 text-sm text-[#A29DB0] hover:bg-white/10 hover:text-white transition-colors duration-200"
          >
            Login
          </a>
          <a
            data-testid="header-signup-cta"
            href="https://becc.takumiwhm.com/auth/register?tenantHost=becc.takumiwhm.com&returnUrl=https%3A%2F%2Fbecc.takumiwhm.com"
            target="_blank"
            rel="noreferrer"
            className="group px-5 py-2 rounded-full bg-[#FF8A00] text-[#12071F] text-sm font-semibold hover:bg-[#FF9F2E] transition-colors duration-200 flex items-center gap-1.5"
          >
            Free Mailbox
            <ArrowUpRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-[#1C0D2C] border-t border-white/10 px-5 py-6 flex flex-col gap-4"
        >
          {NAV.map((item) => (
            <button
              key={item.id}
              data-testid={`mobile-nav-${item.id}`}
              onClick={() => {
                setOpen(false);
                scrollTo(item.id);
              }}
              className="text-left text-base text-[#A29DB0] hover:text-white transition-colors duration-200"
            >
              {item.label}
            </button>
          ))}
          <div className="flex gap-3 pt-2">
            <a
              data-testid="mobile-login-link"
              href="https://bec.tasoko.com/login"
              target="_blank"
              rel="noreferrer"
              className="flex-1 text-center px-5 py-2.5 rounded-full border border-white/20 text-sm text-[#A29DB0]"
            >
              Login
            </a>
            <a
              data-testid="mobile-signup-cta"
              href="https://becc.takumiwhm.com/auth/register?tenantHost=becc.takumiwhm.com&returnUrl=https%3A%2F%2Fbecc.takumiwhm.com"
              target="_blank"
              rel="noreferrer"
              className="flex-1 text-center px-5 py-2.5 rounded-full bg-[#FF8A00] text-[#12071F] text-sm font-semibold"
            >
              Free Mailbox
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
